import React, { useState, useRef, useCallback, useEffect } from 'react';
import { collection, addDoc } from 'firebase/firestore';
import { db } from '../../config/firebaseConfig';
import { toast } from 'react-toastify';
import styles from './AddExternalPersonnelModal.module.css';
import { FaUserPlus, FaTimes, FaSave, FaExclamationTriangle } from 'react-icons/fa';
import { useTheme } from '../../contexts/ThemeContext';

const statusOptions = [
  { value: 'aktif', label: 'aktif' },
  { value: 'inaktif', label: 'inaktif' }
];

const AddExternalPersonnelModal = ({ onClose, onUpdate }) => {
  const { isDarkMode } = useTheme();
  const modalRef = useRef(null);
  const modalBodyRef = useRef(null);
  const [formData, setFormData] = useState({
    'Adı': '',
    'Soyadı': '',
    'Adres': '',
    'Telefon': '',
    'E-Posta': '',
    'Kurum': '',
    'Ünvan': '',
    'Uzmanlık': '',
    'Ana Bilim Dalı': '',
    'Personel Tipi': '',
    'Durum': 'aktif',
    'Bilgi': ''
  });
  const [fieldErrors, setFieldErrors] = useState({});
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);
  
  // Add escape key listener to close modal
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [onClose]);

  const validateField = (name, value) => {
    return value && value.toString().trim() !== '';
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error for this field if it's valid now
    if (attemptedSubmit && validateField(name, value)) {
      setFieldErrors(prev => ({
        ...prev,
        [name]: false
      }));
    }
  };

  const validateForm = () => {
    const errors = {};
    let isValid = true;
    
    Object.entries(formData).forEach(([fieldName, value]) => {
      const valid = validateField(fieldName, value);
      errors[fieldName] = !valid;
      if (!valid) isValid = false;
    });
    
    setFieldErrors(errors);
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setAttemptedSubmit(true);
    
    if (!validateForm()) {
      const emptyFields = Object.entries(fieldErrors)
        .filter(([_, hasError]) => hasError)
        .map(([fieldName]) => fieldName);
      
      toast.warn(
        <div>
          <div style={{display: 'flex', alignItems: 'center', marginBottom: '8px'}}>
            <FaExclamationTriangle style={{marginRight: '8px'}} />
            <strong>Eksik Bilgiler</strong>
          </div>
          <p>Lütfen aşağıdaki alanları doldurun:</p>
          <ul style={{marginLeft: '20px', marginTop: '5px'}}>
            {emptyFields.map(field => (
              <li key={field}>{field}</li>
            ))}
          </ul>
        </div>,
        {
          position: "top-center",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true
        }
      );
      
      // Scroll to first error field
      const firstErrorField = document.querySelector(`[name="${emptyFields[0]}"]`);
      if (firstErrorField) {
        firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
        firstErrorField.focus();
      }
      
      return;
    }
    
    try {
      await addDoc(collection(db, 'ExternalPersonnels'), formData);
      toast.success('Harici personel başarıyla eklendi!');
      onUpdate(); // Refresh the personnel list
      onClose();
    } catch (error) {
      console.error('Error adding external personnel:', error);
      toast.error('Harici personel eklenirken bir hata oluştu!');
    }
  };

  return (
    <div 
      className={styles.modalOverlay} 
      ref={modalRef}
      data-theme={isDarkMode ? 'dark' : 'light'}
    >
      <div className={styles.modalContent}>
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>
            <FaUserPlus className={styles.modalIcon} />
            Yeni Harici Personel Ekle
          </h2>
          <button 
            className={styles.closeButton} 
            onClick={onClose}
            aria-label="Kapat"
          >
            <FaTimes />
          </button>
        </div>
        
        <form onSubmit={handleSubmit}>
          <div className={styles.modalBody} ref={modalBodyRef}>
            <div className={styles.formGrid}>
              {/* Personal Information */}
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Adı:</label>
                <input
                  type="text"
                  name="Adı"
                  value={formData['Adı']}
                  onChange={handleChange}
                  required
                  className={`${styles.formInput} ${attemptedSubmit && fieldErrors['Adı'] ? styles.inputError : ''}`}
                />
                {attemptedSubmit && fieldErrors['Adı'] && (
                  <div className={styles.errorText}>Bu alan zorunludur</div>
                )}
              </div>
              
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Soyadı:</label>
                <input
                  type="text"
                  name="Soyadı"
                  value={formData['Soyadı']}
                  onChange={handleChange}
                  required
                  className={`${styles.formInput} ${attemptedSubmit && fieldErrors['Soyadı'] ? styles.inputError : ''}`}
                />
                {attemptedSubmit && fieldErrors['Soyadı'] && (
                  <div className={styles.errorText}>Bu alan zorunludur</div>
                )}
              </div>
              
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>E-posta:</label>
                <input
                  type="email"
                  name="E-Posta"
                  value={formData['E-Posta']}
                  onChange={handleChange}
                  className={`${styles.formInput} ${attemptedSubmit && fieldErrors['E-Posta'] ? styles.inputError : ''}`}
                />
                {attemptedSubmit && fieldErrors['E-Posta'] && (
                  <div className={styles.errorText}>Bu alan zorunludur</div>
                )}
              </div>
              
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Telefon:</label>
                <input
                  type="tel"
                  name="Telefon"
                  value={formData['Telefon']}
                  onChange={handleChange}
                  className={`${styles.formInput} ${attemptedSubmit && fieldErrors['Telefon'] ? styles.inputError : ''}`}
                />
                {attemptedSubmit && fieldErrors['Telefon'] && (
                  <div className={styles.errorText}>Bu alan zorunludur</div>
                )}
              </div>
              
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Adres:</label>
                <textarea
                  name="Adres"
                  value={formData['Adres']}
                  onChange={handleChange}
                  className={`${styles.formTextarea} ${attemptedSubmit && fieldErrors['Adres'] ? styles.inputError : ''}`}
                  rows="3"
                />
                {attemptedSubmit && fieldErrors['Adres'] && (
                  <div className={styles.errorText}>Bu alan zorunludur</div>
                )}
              </div>
              
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Kurum:</label>
                <input
                  type="text"
                  name="Kurum"
                  value={formData['Kurum']}
                  onChange={handleChange}
                  className={`${styles.formInput} ${attemptedSubmit && fieldErrors['Kurum'] ? styles.inputError : ''}`}
                />
                {attemptedSubmit && fieldErrors['Kurum'] && (
                  <div className={styles.errorText}>Bu alan zorunludur</div>
                )}
              </div>
              
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Ünvan:</label>
                <input
                  type="text"
                  name="Ünvan"
                  value={formData['Ünvan']}
                  onChange={handleChange}
                  className={`${styles.formInput} ${attemptedSubmit && fieldErrors['Ünvan'] ? styles.inputError : ''}`}
                />
                {attemptedSubmit && fieldErrors['Ünvan'] && (
                  <div className={styles.errorText}>Bu alan zorunludur</div>
                )}
              </div>
              
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Uzmanlık:</label>
                <input
                  type="text"
                  name="Uzmanlık"
                  value={formData['Uzmanlık']}
                  onChange={handleChange}
                  className={`${styles.formInput} ${attemptedSubmit && fieldErrors['Uzmanlık'] ? styles.inputError : ''}`}
                />
                {attemptedSubmit && fieldErrors['Uzmanlık'] && (
                  <div className={styles.errorText}>Bu alan zorunludur</div>
                )}
              </div>
              
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Ana Bilim Dalı:</label>
                <input
                  type="text"
                  name="Ana Bilim Dalı"
                  value={formData['Ana Bilim Dalı']}
                  onChange={handleChange}
                  className={`${styles.formInput} ${attemptedSubmit && fieldErrors['Ana Bilim Dalı'] ? styles.inputError : ''}`}
                />
                {attemptedSubmit && fieldErrors['Ana Bilim Dalı'] && (
                  <div className={styles.errorText}>Bu alan zorunludur</div>
                )}
              </div>
              
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Personel Tipi:</label>
                <input
                  type="text"
                  name="Personel Tipi"
                  value={formData['Personel Tipi']}
                  onChange={handleChange}
                  className={`${styles.formInput} ${attemptedSubmit && fieldErrors['Personel Tipi'] ? styles.inputError : ''}`}
                />
                {attemptedSubmit && fieldErrors['Personel Tipi'] && (
                  <div className={styles.errorText}>Bu alan zorunludur</div>
                )}
              </div>
              
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Durum:</label>
                <select
                  name="Durum"
                  value={formData['Durum']}
                  onChange={handleChange}
                  className={`${styles.formSelect} ${attemptedSubmit && fieldErrors['Durum'] ? styles.inputError : ''}`}
                >
                  {statusOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                {attemptedSubmit && fieldErrors['Durum'] && (
                  <div className={styles.errorText}>Bu alan zorunludur</div>
                )}
              </div>
              
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Bilgi:</label>
                <input
                  type="text"
                  name="Bilgi"
                  value={formData['Bilgi']}
                  onChange={handleChange}
                  className={`${styles.formInput} ${attemptedSubmit && fieldErrors['Bilgi'] ? styles.inputError : ''}`}
                />
                {attemptedSubmit && fieldErrors['Bilgi'] && (
                  <div className={styles.errorText}>Bu alan zorunludur</div>
                )}
              </div>
            </div>
          </div>
          
          <div className={styles.formActions}>
            <button 
              type="button" 
              className={styles.buttonCancel}
              onClick={onClose}
            >
              İptal
            </button>
            <button 
              type="submit" 
              className={styles.buttonSave}
            >
              <FaSave className={styles.buttonIcon} />
              Kaydet
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddExternalPersonnelModal; 